/**
 * PR-D4-031 — TestPage shell using MadCrab_V SatTest layout classes.
 * PR-D4-062 — Universal Bluebook split layout via QuestionCard / UniversalSplitLayout.
 * Verbal loads via PR-D4-030 POST /api/questions/fast-batch.
 */

import { useEffect, useCallback, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTest } from '../hooks/useTest';
import { useTimer } from '../hooks/useTimer';
import { QuestionCard } from '../components/QuestionCard';
import type { TestMode, DifficultyTier } from '../lib/types';
import { resolveTelemetryUserId } from '../lib/auth-session';
import { api } from '../lib/api-client';
import '../madcrab/SatTest.css';

function parseMode(value: string | null): TestMode {
    if (value === 'full_practice' || value === 'spartan') return value;
    return 'training';
}

export function TestPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const mode = parseMode(searchParams.get('mode'));
    const topic = searchParams.get('topic') || 'verbal/craft_structure';
    const tier = (searchParams.get('tier') as DifficultyTier) || 'T2';
    const difficulty = searchParams.get('difficulty') || 'easy';
    const urlStudentId = searchParams.get('studentId');
    const studentId = resolveTelemetryUserId(urlStudentId);
    const smartTraining = searchParams.get('smart') === '1';

    const test = useTest();
    const [isFinishing, setIsFinishing] = useState(false);
    const timer = useTimer({
        totalSeconds: test.totalTimeSeconds || 1,
        onTimeUp: () => test.finishTest(),
    });

    useEffect(() => {
        const resume = searchParams.get('resume') === '1';
        if (resume) {
            void (async () => {
                try {
                    const payload = await api.getActiveProgressSession();
                    if (payload.session?.moduleType === 'verbal' && test.restoreServerSession(payload.session, studentId)) {
                        return;
                    }
                } catch {
                    // Fall through to dashboard if the draft cannot be restored.
                }
                navigate('/dashboard');
            })();
            return;
        }
        test.loadTest({ mode, studentId, topic, tier, difficulty, smartTraining });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (test.status === 'in-progress' && test.timerEnabled) {
            const elapsed = test.startTime
                ? Math.max(0, Math.floor((Date.now() - test.startTime) / 1000))
                : 0;
            timer.restoreElapsed(elapsed);
            timer.start();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [test.status, test.timerEnabled]);

    useEffect(() => {
        if (test.status === 'completed') {
            navigate('/results', {
                state: {
                    questions: test.questions,
                    answers: Array.from(test.answers.entries()),
                    results: test.results,
                    score: test.score,
                    total: test.questions.length,
                    mode: test.mode,
                    sessionId: test.sessionId,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [test.status]);

    const handleStart = useCallback(async () => {
        await test.startTest();
        if (test.timerEnabled) {
            timer.reset();
            timer.start();
        }
    }, [test, timer, test.timerEnabled]);

    const handleFinish = useCallback(async () => {
        if (isFinishing) return;
        setIsFinishing(true);
        await test.finishTest();
    }, [isFinishing, test]);

    const handleQuestionSubmit = useCallback(() => {
        if (test.currentIndex === test.questions.length - 1) {
            void handleFinish();
            return;
        }
        test.submitAnswer();
    }, [handleFinish, test]);

    if (test.status === 'loading') {
        return (
            <div className="sat-root sat-root--viewport">
                <div className="sat-loading">Loading questions…</div>
            </div>
        );
    }

    if (test.status === 'error') {
        return (
            <div className="sat-root">
                <div className="sat-error">
                    <p>
                        <strong>Generation Error.</strong> {test.error}
                    </p>
                    <p className="sat-error__hint">
                        Ensure the Verbal API is reachable (POST /api/questions/fast-batch →
                        :8787).
                    </p>
                    <button
                        type="button"
                        className="sat-topbar-badge"
                        onClick={() => navigate('/dashboard')}
                        style={{ marginTop: 16 }}
                    >
                        Return to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    if (test.status === 'ready') {
        return (
            <div className="sat-root" style={{ alignItems: 'center', justifyContent: 'center' }}>
                <div className="app-hub-card" style={{ maxWidth: 420, margin: '2rem auto' }}>
                    <p className="app-hub-brand">SAT Generator</p>
                    <h2 className="app-hub-title">Ready to Start</h2>
                    <p className="app-hub-hint">
                        Topic: {topic}
                        <br />
                        Difficulty: {difficulty}
                        <br />
                        Questions: {test.questions.length}
                    </p>
                    <button type="button" className="app-hub-primary" onClick={handleStart}>
                        Begin Test
                    </button>
                </div>
            </div>
        );
    }

    if (test.status === 'in-progress' && test.currentQuestion) {
        return (
            <div className="sat-root sat-root--viewport">
                <QuestionCard
                    question={test.currentQuestion}
                    questionNumber={test.currentIndex + 1}
                    totalQuestions={test.questions.length}
                    selectedOption={
                        test.answers.has(test.currentIndex)
                            ? test.answers.get(test.currentIndex) ?? null
                            : null
                    }
                    onSelectOption={test.selectOption}
                    onSubmit={handleQuestionSubmit}
                    showFeedback={test.mode === 'training'}
                    sectionTitle="Reading and Writing"
                    onSaveDraft={async () => {
                        await test.flushSessionStep();
                    }}
                    onExit={() => {
                        navigate('/dashboard');
                    }}
                    onNavigateToQuestion={test.goToQuestion}
                    answeredQuestionIndexes={new Set(test.answers.keys())}
                />

                <footer className="sat-test__footer test-nav-bar trainer-bottom-bar">
                    <div className="bar-left flex items-center gap-4">
                        <button
                            type="button"
                            className="sat-back"
                            onClick={() => test.goToQuestion(test.currentIndex - 1)}
                            disabled={test.currentIndex === 0}
                            aria-label="Back / Назад"
                        >
                            Back
                        </button>
                    </div>
                    <div className="bar-right flex items-center gap-4">
                        {test.currentIndex === test.questions.length - 1 ? (
                            <button
                                type="button"
                                className="sat-next sat-finish-test"
                                onClick={() => void handleFinish()}
                                disabled={isFinishing}
                                aria-label="Finish Test / Завершить"
                            >
                                {isFinishing ? 'Saving…' : 'Finish Test / Завершить'}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="sat-next"
                                onClick={() => test.goToQuestion(test.currentIndex + 1)}
                                aria-label="Next / Далее"
                            >
                                Next
                            </button>
                        )}
                    </div>
                </footer>
            </div>
        );
    }

    return null;
}
